import {useCallback, useEffect, useRef, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useIVStore, DEFAULT_IMAGE_DETAILS, IVStoreState} from '@chekin/shared';
import {
  getDetectedIvBypassAge,
  useGuestSchema,
  type AddFormType,
} from '@chekin/shared/guest-form';
import {STORAGE_KEYS} from 'utils/constants';
import {saveIVScan} from './ivLockStore';
import {useGetGuestId} from 'hooks/useGetGuestId';
import {getFeatureFlags, IVIntent, QUERY_CACHE_KEYS, SessionStorage} from '@chekin/core';
import {useIsRemoteIV} from 'hooks/iv/useRemoteStartVerificationLink';
import useBuildOCRDetectedData from 'hooks/useBuildOCRDetectedData';
import {API} from '@chekin/api';
import {
  getIVIntentRemoteSession,
  setIVIntentRemoteSession,
} from './ivIntentRemoteSession';

export {
  setIVIntentRemoteSession,
  getIVIntentRemoteSession,
  removeIVIntentRemoteSession,
} from './ivIntentRemoteSession';

export const convertImagesDetailsToStoreState = (
  imagesDetails?: IVIntent['image_details'],
) => {
  if (!imagesDetails) {
    return {imagesDetails: DEFAULT_IMAGE_DETAILS};
  }

  return {
    imagesDetails: {
      ...DEFAULT_IMAGE_DETAILS,
      ...imagesDetails,
    },
  };
};

export const convertIvIntentToStoreState = (
  ivIntentItem: IVIntent,
  formFields: AddFormType | undefined,
): Partial<IVStoreState> => {
  const detectedBypassAge = ivIntentItem.document_passed
    ? getDetectedIvBypassAge(ivIntentItem.mrz, formFields)
    : null;
  return {
    setupData: {
      nationality: ivIntentItem.nationality,
      documentType: ivIntentItem.type_doc,
    },
    ocrResultData: {
      ocrPassed: Boolean(ivIntentItem.document_passed),
    },
    isIvFailed: ivIntentItem.is_iv_failed,
    isPropertyQRScanned: ivIntentItem.is_property_qr_scanned,
    livenessResultData: ivIntentItem.liveness_data,
    biomatchResultData: {
      biomatchPassed: Boolean(ivIntentItem.biomatch_passed),
      distance: null,
    },
    ...convertImagesDetailsToStoreState(ivIntentItem.image_details),
    ...(detectedBypassAge != null ? {declaredIvBypassAge: detectedBypassAge} : {}),
  };
};

export const useIVRemoteSync = () => {
  const {buildFormData} = useBuildOCRDetectedData();
  const guestId = useGetGuestId();
  const {intentId: urlIntentId} = useIsRemoteIV();
  const {sync, setRemoteSession} = useIVStore();
  const [isLoaded, setIsLoaded] = useState(false);
  const {formFields} = useGuestSchema();
  const formFieldsRef = useRef(formFields);
  formFieldsRef.current = formFields;

  const loadRemoteState = useCallback(
    async ({postHandler}: {postHandler?: () => void}) => {
      const intentId = getIVIntentRemoteSession();
      if (intentId) {
        const data = await API.identityVerification.getIvIntent(intentId);
        sync(convertIvIntentToStoreState(data, formFieldsRef.current));

        if (data?.mrz) {
          const builtFormData = buildFormData(data.mrz);
          SessionStorage.set(STORAGE_KEYS.personalFormData, builtFormData);
          saveIVScan(builtFormData, guestId);
        }

        postHandler?.();
      }
    },
    [buildFormData, sync, guestId],
  );

  const isRemoteIVEnabled = getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE');
  const intentId = getIVIntentRemoteSession() || urlIntentId;

  useEffect(() => {
    if (urlIntentId && isRemoteIVEnabled) {
      setRemoteSession(true);
      setIVIntentRemoteSession(urlIntentId);
      API.identityVerification.startIVOnMobile(urlIntentId);
    }
  }, [urlIntentId, setRemoteSession, isRemoteIVEnabled]);

  const {data: ivIntentData, isError: isIvIntentError} = useQuery<IVIntent>({
    queryKey: [QUERY_CACHE_KEYS.ivIntent, intentId],
    queryFn: () => API.identityVerification.getIvIntent(intentId || ''),
    enabled: isRemoteIVEnabled && Boolean(intentId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (!isRemoteIVEnabled || !intentId) {
      setIsLoaded(true);
      return;
    }
    if (ivIntentData) {
      sync(convertIvIntentToStoreState(ivIntentData, formFieldsRef.current));
      setIsLoaded(true);
    } else if (isIvIntentError) {
      setIsLoaded(true);
    }
  }, [isRemoteIVEnabled, intentId, ivIntentData, isIvIntentError, sync]);

  return {
    isLoaded: isRemoteIVEnabled ? isLoaded : true,
    loadRemoteState,
  };
};
